/**
 * @acsam/crypto v1.4.4
 * Cryptography used in ACSAM
 *
 * @author Jens Oswald
 * @license UNLICENSED
 * @preserve
 */

/* eslint-disable no-bitwise */
// Create a polyfill for Math.clz32 (https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Math/clz32#Polyfill)
const clz32Polyfill = x => 31 - Math.log(x >>> 0) / Math.LN2 | 0;
// Create a shortcut
const clz32 = Math.clz32 || clz32Polyfill;
/**
 * Replacer for the text decoder
 *
 * @param {String} encoded - Encoded string
 */
const decoderReplacer = (encoded) => {
  let codePoint = encoded.charCodeAt(0) << 24;
  const leadingOnes = clz32(~codePoint) | 0;
  let endPos = 0;
  const stringLen = encoded.length | 0;
  let result = '';
  if (leadingOnes < 5 && stringLen >= leadingOnes) {
    codePoint = (codePoint << leadingOnes) >>> (24 + leadingOnes);
    for (endPos = 1; endPos < leadingOnes; endPos = endPos + 1 | 0) {
      codePoint = (codePoint << 6) | (encoded.charCodeAt(endPos) & 0x3f);
    }
    if (codePoint <= 0xFFFF) {
      // BMP code point
      result += String.fromCharCode(codePoint);
    } else if (codePoint <= 0x10FFFF) {
      // https://mathiasbynens.be/notes/javascript-encoding#surrogate-formulae
      codePoint = codePoint - 0x10000 | 0;
      result += String.fromCharCode(
        (codePoint >> 10) + 0xD800 | 0, // highSurrogate
        (codePoint & 0x3ff) + 0xDC00 | 0, // lowSurrogate
      );
    } else {
      // to fill it in with INVALIDs
      endPos = 0;
    }
  }
  for (; endPos < stringLen; endPos = endPos + 1 | 0) {
    // replacement character
    result += '\ufffd';
  }
  return result;
};
/**
 * Replaces no ascii chars with it's real counterpart
 *
 * @param {String} nonAsciiChars - Input string to replace non ascii chars
 * @return {String} Text with replaced none ascii chars
 */
const encoderReplacer = (nonAsciiChars) => {
  // Make the UTF string into a binary UTF-8 encoded string
  let point = nonAsciiChars.charCodeAt(0) | 0;
  if (point >= 0xD800 && point <= 0xDBFF) {
    const nextCode = nonAsciiChars.charCodeAt(1) | 0;
    // eslint-disable-next-line no-self-compare
    if (nextCode !== nextCode) {
      // NaN because string is 1 code point long
      return String.fromCharCode(0xef, 0xbf, 0xbd);
    }
    // https://mathiasbynens.be/notes/javascript-encoding#surrogate-formulae
    if (nextCode >= 0xDC00 && nextCode <= 0xDFFF) {
      point = ((point - 0xD800) << 10) + nextCode - 0xDC00 + 0x10000 | 0;
      if (point > 0xffff) {
        return String.fromCharCode(
          (0x1e << 3) | (point >>> 18),
          (0x2 << 6) | ((point >>> 12) & 0x3f),
          (0x2 << 6) | ((point >>> 6) & 0x3f),
          (0x2 << 6) | (point & 0x3f),
        );
      }
    } else {
      return String.fromCharCode(0xef, 0xbf, 0xbd);
    }
  }
  if (point <= 0x007f) {
    return nonAsciiChars;
  }
  if (point <= 0x07ff) {
    return String.fromCharCode((0x6 << 5) | (point >>> 6), (0x2 << 6) | (point & 0x3f));
  }
  return String.fromCharCode((0xe << 4) | (point >>> 12), (0x2 << 6) | ((point >>> 6) & 0x3f), (0x2 << 6) | (point & 0x3f));
};

/**
 * A TextDecoder polyfill to close the gap of missing support in Edge Browser and IE 11. Adopted from
 * https://github.com/anonyco/FastestSmallestTextEncoderDecoder. Customized for the use in ACSAM. We only need a decoding to UTF-8 so other encodings
 * are not supported
 */
class TextDecoderPolyfill {
  /**
   * Creates a new instance of text decoder
   *
   * @param {String} encoding - Encoding
   */
  constructor(encoding) {
    this.encoding = encoding;
  }

  /**
   * Decodes a given ArrayBuffer or ArrayBufferView to a UTF-8 encoded text
   *
   * @param {ArrayBuffer|ArrayBufferView} data
   * @return {String} UTF-8 encoded text
   */
  decode(data) {
    if (this.encoding !== 'utf-8') {
      throw new TypeError('TextDecoder polyfill only supports utf-8 encoding.');
    }
    const buffer = (data && data.buffer) || data;
    const uint8Data = new Uint8Array(buffer);
    let result = '';
    for (let i = 0, iLen = uint8Data.length | 0; i < iLen; i = i + 32768 | 0) {
      result += String.fromCharCode.apply(0, uint8Data.subarray(i, i + 32768 | 0));
    }
    result = result.replace(/[\xc0-\xff][\x80-\xbf]*/g, decoderReplacer);
    return result;
  }
}

/**
 * A TextEncoder polyfill to close the gap of missing support in Edge Browser and IE 11. Adopted from
 * https://github.com/anonyco/FastestSmallestTextEncoderDecoder. Customized for the use in ACSAM. We only need a encoding from UTF-8 so other
 * encodings are not supported
 */
class TextEncoderPolyfill {
  /**
   * Creates a new instance of text decoder
   *
   * @param {String} encoding - Encoding
   */
  constructor(encoding) {
    this.encoding = encoding;
  }

  /**
   *
   * @param {String} text - UTF-8 encoded text
   * @return {Uint8Array} Binary
   */
  encode(text) {
    if (this.encoding !== 'utf-8') {
      throw new TypeError('TextEncoder polyfill only supports utf-8 encoding.');
    }
    const encodedText = text === undefined
      ? ''
      : (`${text}`).replace(/[\x80-\uD7ff\uDC00-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]?/g, encoderReplacer);
    const iLen = encodedText.length | 0;
    const result = new Uint8Array(iLen);
    for (let i = 0; i < iLen; i = i + 1 | 0) {
      result[i] = encodedText.charCodeAt(i);
    }
    return result;
  }
}

// Use text encoding polyfills when necessary
const TextDecoder = window.TextDecoder || TextDecoderPolyfill;
const TextEncoder = window.TextEncoder || TextEncoderPolyfill;
// Extract crypto.subtle and crypto.getRandomValues function (getRandomValues) from window.crypto
// A pre computation array for holding the 2-character hex octets for every possible value of an unsigned byte: [0, 255]
const BYTE_TO_HEX = [];
// Pre computes a lookup table for encoding a binary to a hex encode string
// eslint-disable-next-line no-plusplus
for (let n = 0; n <= 0xff; ++n) {
  const hexValue = n.toString(16);
  // Pad or not?
  const hexOctet = hexValue.length < 2 ? `0${hexValue}` : hexValue;
  BYTE_TO_HEX.push(hexOctet);
}
const PEM_HEADER_FOOTER_REGEX = /(-+(BEGIN|END) (PUBLIC|PRIVATE) KEY-+)(.*?)/g;
/**
 * Password-Based Key Derivation Function 2 which is compliant to RFC 8018. Please keep in mind that you have to provide salt when deriving a key
 * for decrypting.
 *
 * @param {String} secret - A shared secret (password or a key)
 * @param {Uint8Array} [salt] - Optional salt. When not defined it will generate a crypto.getRandomValues salt
 * @return {Promise<{key: CryptoKey, salt: Uint8Array}>}
 */
const pbkdf2 = (secret, salt) => new Promise((resolve, reject) => {
  // Create a text encoder instance
  const encoder = new TextEncoder('utf-8');
  // Encode secret to Uint8Array (binary)
  const secretBuffer = encoder.encode(secret);
  // Key length of derivation is always 48 bytes (32 bytes for the key, 16 bytes for the initialization vector)
  const keyLength = 48;
  // Import secret and resolve with a crypto key
  crypto.subtle
    .importKey('raw', secretBuffer, { name: 'PBKDF2' }, false, ['deriveBits'])
    .then((importedKey) => {
      // Create a salt buffer either from the provided salt (decryption) or a crypto.getRandomValuesly chosen one (encryption)
      const saltBuffer = salt || crypto.getRandomValues(new Uint8Array(16));
      // Specify the used algorithm
      const algorithm = {
        name: 'PBKDF2',
        hash: 'SHA-256',
        salt: saltBuffer,
        iterations: 1000,
      };
      // Derive 384 bits from the crypto key (256 bits as key and 128 bits as vi)
      // Move to next step on resolve with derived bits and the salt buffer (needed!)
      return crypto.subtle.deriveBits(algorithm, importedKey, keyLength * 8)
        .then(derivedBits => ({ derivedBits, salt: saltBuffer }));
    })
    .then((derivation) => {
      // Key length is always 32 bytes
      const derivedKeyLength = 32;
      // Extract salt and derived bits from the step before
      const { salt: saltBuffer, derivedBits } = derivation;
      // Separated key and iv
      const derivedKey = derivedBits.slice(0, derivedKeyLength);
      const iv = derivedBits.slice(derivedKeyLength);
      // Import derived key and resolve with cipher parameters (key, iv and salt)
      return crypto.subtle
        .importKey('raw', derivedKey, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt'])
        .then(key => ({ key, iv, salt: saltBuffer }));
    })
    .then(cipherParams => resolve(cipherParams))
    .catch(error => reject(error));
});

/**
 * Takes a base64 encoded string an convert it to a binary
 *
 * @param {String} b64str - A base64 encoded string
 * @return {ArrayBufferLike} A binary
 */
const base64StringToArrayBuffer = (b64str) => {
  const byteStr = atob(b64str);
  const bytes = new Uint8Array(byteStr.length);
  for (let i = 0; i < byteStr.length; i += 1) {
    bytes[i] = byteStr.charCodeAt(i);
  }
  return bytes.buffer;
};
/**
 * Fastest known implementation of converting a array buffer (binary) into hex encoded string. This approach uses the pre computed 2-character hex
 * octets lookup table (BYTE_TO_HEX constant) and then just maps each value in the ArrayBuffer through the array of octet strings.
 *
 * @param {ArrayBufferLike} buffer - A array buffer like data structure
 * @return {String} Hex encoded text
 */
const arrayBufferToHexString = (buffer) => {
  const uint8Array = new Uint8Array(buffer);
  const hexOctets = [];
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < uint8Array.length; ++i) {
    hexOctets.push(BYTE_TO_HEX[uint8Array[i]]);
  }
  return hexOctets.join('');
};

/**
 * Converts a array buffer to a UTF-8 string
 *
 * @param {ArrayBufferLike} buffer - A array buffer like data structure
 * @return {String} UTF-8 encode string
 */
const arrayBufferToString = buffer => String.fromCharCode.apply(null, new Uint8Array(buffer));

/**
 * Takes a plain pem encoded key (private, or public) and convert it to a binary
 *
 * @param {String} key - A pem encoded key a plain text
 * @return {Promise<ArrayBuffer>} Binary encoded key
 */
const convertKeyFromPem = key => new Promise((resolve, reject) => {
  try {
    // Remove header and footer from pem encoded key
    const rawKey = key.replace(PEM_HEADER_FOOTER_REGEX, '');
    // Split lines
    const lines = rawKey.split('\n');
    let raw = '';
    // Concat multi line pem file to one raw string when necessary
    if (lines.length > 1) {
      for (let i = 0; i < lines.length; i += 1) {
        raw += lines[i].trim();
      }
    } else {
      raw = rawKey.trim();
    }
    // Encode pem (base64) to binary
    const encoded = base64StringToArrayBuffer(raw);
    // Resolve with binary encoded key
    resolve(encoded);
  } catch (error) {
    reject(error);
  }
});

/**
 *
 * @type {Object}
 */
const format = {
  /**
   * Takes the cipher parameters and converts them to base64 encoded parts which will be concatenated to the cipher
   *
   * @param {Object} cipherParams - The cipher parameters which should by stringified
   * @return {String} Base64 encoded cipher
   */
  stringify(cipherParams) {
    const salt = btoa(String.fromCharCode(...cipherParams.salt));
    const cipherText = btoa(String.fromCharCode(...new Uint8Array(cipherParams.cipherText)));
    return salt + cipherText;
  },
  /**
   * Takes a base64 encoded cipher and splits it up to salt and cipher text which each are encoded to Uint8Array (binary)
   *
   * @param {String} cipher
   * @return {{cipherText: Uint8Array, salt: Uint8Array}}
   */
  parse(cipher) {
    const salt = Uint8Array.from(atob(cipher.slice(0, 24)), c => c.charCodeAt(0));
    const cipherText = Uint8Array.from(atob(cipher.slice(24)), c => c.charCodeAt(0));
    return {
      cipherText,
      salt,
    };
  },
};

class Crypto {
  /**
   * Creates a crypto.getRandomValues secret from a crypto.getRandomValues byte array as base64 encoded text
   *
   * @return {String} Base64 encoded crypto.getRandomValues text
   */
  static createSecret() {
    return btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(16))));
  }

  /**
   * Encrypts a given payload with AES (CBC mode) by deriving a key from the provided secret which is used to encrypt the payload. The key is kept
   * confidential and the result will be a base64 encoded cipher with a prefixed salt.
   *
   * @param {String} payload - The payload to be encrypted
   * @param {String} secret - A shared secret for deriving a key for encryption
   * @return {Promise<String>} - Concatenated iv and cipher text as base64 concatenated string
   */
  static encryptAesCbc(payload, secret) {
    return new Promise((resolve, reject) => {
      const encoder = new TextEncoder('utf-8');
      const data = encoder.encode(payload);
      pbkdf2(secret)
        .then(({ iv, key, salt }) => {
          const algorithm = { name: 'AES-CBC', iv };
          // Encrypt with derived key and resolve with cipher text, iv and salt
          return crypto.subtle.encrypt(algorithm, key, data)
            .then(cipherText => ({ cipherText, iv, salt }));
        })
        .then((cipherParams) => {
          // Format cipher parameter
          resolve(format.stringify(cipherParams));
        })
        .catch(error => reject(error));
    });
  }

  /**
   * Decrypts a given cipher with given secret by deriving a key from the provided secret. The key is computed with the prefixed salt of the provided
   * cipher. The key is kept confidential and is only used within the decryption/encryption process. When no salt is provided via cipher the
   * encryption will fail.
   *
   * @param {String} cipher - Base64 encoded cipher with salt and cipher text
   * @param {String} secret - A shared secret for deriving a key for decryption
   * @return {Promise<unknown>}
   */
  static decryptAesCbc(cipher, secret) {
    return new Promise((resolve, reject) => {
      const decoder = new TextDecoder('utf-8');
      // Extract cipher parameter
      const { salt, cipherText } = format.parse(cipher);
      // Specify algorithm for key generation
      pbkdf2(secret, salt)
        .then(({ key, iv }) => {
          const algorithm = { name: 'AES-CBC', iv };
          return crypto.subtle.decrypt(algorithm, key, cipherText);
        })
        .then(payload => resolve(decoder.decode(payload)))
        .catch(error => reject(error));
    });
  }

  /**
   * Encrypts a given payload with RSA-OAEP. Please consider https://tools.ietf.org/html/rfc3447#page-16 for security and implementation notes. Please
   * keep in mind that the native rsa encryption is not compatible to the legacy encryption. The legacy RSA encryption uses RSASSA-PKCS1-v1_5 as
   * algorithm which can not be changed due to missing implementation in Jsencrypt. Please keep also in mind that the RSA encryption is only suitable
   * for small payloads. With SHA-256 hashing about <= 62 bytes.
   *
   * @param {String} payload - The payload that should be encrypted with RSA
   * @param {String} publicKey - PEM encoded public key
   * @return {Promise<String>} - Base64 encoded cipher text
   */
  static encryptRsa(payload, publicKey) {
    return new Promise((resolve, reject) => {
      // Convert public key from pem format to binary
      convertKeyFromPem(publicKey)
        .then((keyData) => {
          // Specify import algorithm for the public key
          const importAlgorithm = {
            name: 'RSA-OAEP',
            hash: {
              name: 'SHA-256',
            },
          };
          // Import public key
          return crypto.subtle.importKey('spki', keyData, importAlgorithm, false, ['encrypt']);
        })
        .then((importedKey) => {
          // Specify encryption algorithm
          const encryptAlgorithm = {
            name: 'RSA-OAEP',
          };
          // Create text encoder instance
          const encoder = new TextEncoder('utf-8');
          // Encode payload to binary with utf-8
          const data = encoder.encode(payload);
          // Encrypt payload
          return crypto.subtle.encrypt(encryptAlgorithm, importedKey, data);
        })
        .then((cipher) => {
          // Convert cipher (binary) to base64 encoded text
          const cipherText = btoa(String.fromCharCode(...new Uint8Array(cipher)));
          // Resolve with base64 encoded cipher text
          resolve(cipherText);
        })
        .catch(error => reject(error));
    });
  }

  static decryptRsa(cipher, privateKey) {
    return new Promise((resolve, reject) => {
      convertKeyFromPem(privateKey)
        .then((keyData) => {
        // Specify import algorithm for the public key
          const importAlgorithm = {
            name: 'RSA-OAEP',
            hash: {
              name: 'SHA-256',
            },
          };
          // Import public key
          return crypto.subtle.importKey('pkcs8', keyData, importAlgorithm, false, ['decrypt']);
        })
        .then((importedKey) => {
          // Specify encryption algorithm
          const decryptAlgorithm = {
            name: 'RSA-OAEP',
          };
          const data = base64StringToArrayBuffer(cipher);
          // Decrypt cipher
          return crypto.subtle.decrypt(decryptAlgorithm, importedKey, data);
        })
        .then((buffer) => {
          const data = arrayBufferToString(buffer);
          resolve(data);
        })
        .catch(error => reject(error));
    });
  }


  /**
   * A hash method for creating a SHA-1 digest from a given payload. Please use this method only for checksum purposes. Don't be stupid and hash
   * confidential data with this method.
   *
   * @param {String} payload - The payload or message from which a digest (one-way hashing) should be created
   * @return {Promise<String>} - Resolves with the hashed payload a SHA-1 digest
   */
  static hash(payload) {
    return new Promise((resolve, reject) => {
      // Create utf-8 encoder instance
      const encoder = new TextEncoder('utf-8');
      // Encode payload to binary
      const data = encoder.encode(payload);
      // Start creating digest from payload
      crypto.subtle
        .digest('SHA-1', data)
        .then((digest) => {
          // The result of the digest process is digest in binary.
          // We have to encode it to hex to be interoperable with the legacy implementation
          const hexEncodedDigest = arrayBufferToHexString(digest);
          resolve(hexEncodedDigest);
        })
        .catch(error => reject(error));
    });
  }
}

export default Crypto;
